import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useToast,
  VStack,
  Alert,
  AlertIcon,
  Heading,
  Text,
  Card,
  CardBody,
  SimpleGrid,
  FormHelperText,
  Select,
  Divider,
  Badge,
} from '@chakra-ui/react';
import { db } from '../../config/firebase';
import { doc, getDoc, updateDoc, setDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';

export type PaymentStrategy = 
  | 'collect_full_at_booking'
  | 'collect_percentage_at_booking'
  | 'collect_at_delivery';

export interface PaymentSettings {
  paymentStrategy: PaymentStrategy;
  upfrontPercentage?: number; // For percentage strategy (0-100)
  payoutRate?: number; // Percentage for technician payouts (0-100)
  requirePaymentMethod?: boolean;
  allowPartialPayments?: boolean;
  refundPolicy?: string;
}

export interface AppSettings {
  id: string;
  paymentStrategy?: PaymentStrategy;
  upfrontPercentage?: number;
  payoutRate?: number;
  requirePaymentMethod?: boolean;
  allowPartialPayments?: boolean;
  refundPolicy?: string;
  businessHours?: any;
  bookingConstraints?: any;
  serviceAreaSettings?: any;
  bookingPoolSettings?: any;
  holidays?: any[];
  technicianSchedules?: any;
  createdAt: any;
  updatedAt: any;
  updatedBy: string;
}

const DEFAULT_PAYMENT_SETTINGS: PaymentSettings = {
  paymentStrategy: 'collect_full_at_booking',
  upfrontPercentage: 10,
  payoutRate: 70,
  requirePaymentMethod: true,
  allowPartialPayments: false,
  refundPolicy: 'Full refund for cancellations more than 24 hours in advance.',
};

const PaymentSettingsComponent: React.FC = () => {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<PaymentSettings>(DEFAULT_PAYMENT_SETTINGS);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const settingsRef = doc(db, 'appSettings', 'default');
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        const data = settingsSnap.data() as AppSettings;
        setSettings({
          paymentStrategy: data.paymentStrategy || 'collect_full_at_booking',
          upfrontPercentage: data.upfrontPercentage || 10,
          payoutRate: data.payoutRate || 70,
          requirePaymentMethod: data.requirePaymentMethod !== false,
          allowPartialPayments: data.allowPartialPayments || false,
          refundPolicy: data.refundPolicy || 'Full refund for cancellations more than 24 hours in advance.',
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load payment settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof PaymentSettings, value: any) => {
    setSettings(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!currentUser) {
      toast({
        title: 'Error',
        description: 'You must be logged in to save settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validate settings
    if (settings.paymentStrategy === 'collect_percentage_at_booking') {
      if (!settings.upfrontPercentage || settings.upfrontPercentage < 0 || settings.upfrontPercentage > 100) {
        toast({
          title: 'Validation Error',
          description: 'Upfront percentage must be between 0 and 100',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }

    if (settings.payoutRate && (settings.payoutRate < 0 || settings.payoutRate > 100)) {
      toast({
        title: 'Validation Error',
        description: 'Payout rate must be between 0 and 100',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    try {
      const settingsRef = doc(db, 'appSettings', 'default');
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        await updateDoc(settingsRef, {
          paymentStrategy: settings.paymentStrategy,
          upfrontPercentage: settings.upfrontPercentage,
          payoutRate: settings.payoutRate,
          requirePaymentMethod: settings.requirePaymentMethod,
          allowPartialPayments: settings.allowPartialPayments,
          refundPolicy: settings.refundPolicy,
          updatedAt: Timestamp.now(),
          updatedBy: currentUser.uid,
        });
      } else {
        await setDoc(settingsRef, {
          id: 'default',
          ...settings,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          updatedBy: currentUser.uid,
        });
      }

      toast({
        title: 'Success',
        description: 'Payment settings updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save payment settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(DEFAULT_PAYMENT_SETTINGS);
    toast({
      title: 'Reset',
      description: 'Payment settings reset to defaults',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  };

  const getStrategyDescription = (strategy: PaymentStrategy) => {
    switch (strategy) {
      case 'collect_full_at_booking':
        return 'Charge the full amount when booking is confirmed';
      case 'collect_percentage_at_booking':
        return `Charge ${settings.upfrontPercentage}% upfront, remainder at delivery`;
      case 'collect_at_delivery':
        return 'No payment required at booking, collect full amount at delivery';
      default:
        return '';
    }
  };

  if (loading) {
    return <Text>Loading...</Text>;
  }

  return (
    <Box>
      <Box mb={6}>
        <Heading 
          size={{ base: 'md', md: 'lg' }}
          mb={4}
          pl={{ base: '60px', lg: 0 }}
          pr={{ base: 4, lg: 0 }}
        >
          Payment Settings
        </Heading>
        
        <Alert status="info" mb={4}>
          <AlertIcon />
          <Box>
            <Text fontSize="sm">
              Configure how and when customers are charged for services.
              These settings affect all new bookings.
            </Text>
          </Box>
        </Alert>
      </Box>

      <Card mb={6}>
        <CardBody>
          <VStack spacing={6} align="stretch">
            <FormControl>
              <FormLabel fontWeight="bold">Payment Strategy</FormLabel>
              <Select
                value={settings.paymentStrategy}
                onChange={(e) => handleChange('paymentStrategy', e.target.value as PaymentStrategy)}
              >
                <option value="collect_full_at_booking">Collect Full Amount at Booking</option>
                <option value="collect_percentage_at_booking">Collect Percentage at Booking</option>
                <option value="collect_at_delivery">Collect at Delivery</option>
              </Select>
              <FormHelperText>{getStrategyDescription(settings.paymentStrategy)}</FormHelperText>
            </FormControl>

            {settings.paymentStrategy === 'collect_percentage_at_booking' && (
              <FormControl>
                <FormLabel>Upfront Percentage (%)</FormLabel>
                <NumberInput
                  value={settings.upfrontPercentage}
                  onChange={(_, val) => handleChange('upfrontPercentage', val)}
                  min={0}
                  max={100}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>
                  Percentage of total amount to collect when booking is confirmed
                </FormHelperText>
              </FormControl>
            )}

            <Divider />

            <Heading size="sm">Technician Payouts</Heading>

            <FormControl>
              <FormLabel>Technician Payout Rate (%)</FormLabel>
              <NumberInput
                value={settings.payoutRate}
                onChange={(_, val) => handleChange('payoutRate', val)}
                min={0}
                max={100}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <FormHelperText>
                Percentage of collected payment that goes to technicians (e.g., 70% = $70 for $100 booking)
              </FormHelperText>
            </FormControl>

            <Divider />

            <Heading size="sm">Payment Requirements</Heading>

            <FormControl display="flex" alignItems="center">
              <FormLabel mb={0} flex="1">
                Require Payment Method
              </FormLabel>
              <input
                type="checkbox"
                checked={settings.requirePaymentMethod}
                onChange={(e) => handleChange('requirePaymentMethod', e.target.checked)}
                style={{ width: '20px', height: '20px' }}
              />
            </FormControl>
            <Text fontSize="sm" color="gray.600">
              Customers must provide a payment method to complete booking
            </Text>

            <FormControl display="flex" alignItems="center">
              <FormLabel mb={0} flex="1">
                Allow Partial Payments
              </FormLabel>
              <input
                type="checkbox"
                checked={settings.allowPartialPayments}
                onChange={(e) => handleChange('allowPartialPayments', e.target.checked)}
                style={{ width: '20px', height: '20px' }}
              />
            </FormControl>
            <Text fontSize="sm" color="gray.600">
              Allow customers to pay remaining balance in installments
            </Text>

            <Divider />

            <Heading size="sm">Refund Policy</Heading>

            <FormControl>
              <FormLabel>Refund Policy</FormLabel>
              <Input
                value={settings.refundPolicy}
                onChange={(e) => handleChange('refundPolicy', e.target.value)}
                placeholder="Enter refund policy details..."
              />
              <FormHelperText>
                Policy text shown to customers during booking
              </FormHelperText>
            </FormControl>
          </VStack>
        </CardBody>
      </Card>

      <Alert status="warning" mb={6}>
        <AlertIcon />
        <Box>
          <Text fontSize="sm" fontWeight="medium" mb={1}>
            Important: Changes to payment strategy
          </Text>
          <Text fontSize="sm">
            Switching payment strategies will affect all new bookings immediately.
            Existing bookings will not be affected.
          </Text>
        </Box>
      </Alert>

      <HStack spacing={3}>
        <Button
          colorScheme="blue"
          onClick={handleSave}
          isLoading={saving}
          loadingText="Saving"
        >
          Save Payment Settings
        </Button>
        <Button
          variant="outline"
          onClick={handleReset}
        >
          Reset to Defaults
        </Button>
      </HStack>
    </Box>
  );
};

export default PaymentSettingsComponent;

